// Login Page for Iron Forums

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/useAuth';
import { Eye, EyeOff, Fingerprint, Mail, ScanFace, X } from 'lucide-react';
import { authAPI } from '../api/client';
import type { TwoFactorPrompt } from '../types';
import { getBiometryLabel } from '../native/biometric';
import BrandLogo from '../components/BrandLogo';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<{ username: string; refreshToken: string } | null>(null);
  const biometricAttempted = useRef(false);
  const [twoFactor, setTwoFactor] = useState<TwoFactorPrompt | null>(null);
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [resendIn, setResendIn] = useState(0);

  const {
    login,
    completeTwoFactor,
    loginWithBiometric,
    biometricAvailable,
    biometricEnabled,
    biometryType,
    enableBiometricLogin,
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    if (biometricEnabled && !biometricAttempted.current) {
      biometricAttempted.current = true;
      handleBiometricLogin();
    }
  }, [biometricEnabled]);

  const handleBiometricLogin = async () => {
    setError('');
    setIsLoading(true);
    try {
      await loginWithBiometric();
      navigate('/');
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('Session expired. Please sign in with your password.');
      } else {
        setError('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const prompt = await login(username, password);
      if (prompt) {
        setTwoFactor(prompt);
        setCode('');
        setResendIn(30);
        setIsLoading(false);
        return;
      }
      afterSignIn();
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const isServerError = status === 502 || status === 503 || status === 504;
        const isNetworkOrTimeout = err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK' || !err.response;
        const message = isServerError
          ? 'Backend is unavailable (server error). Try again in a moment.'
          : isNetworkOrTimeout
            ? 'Could not reach server. Check that the backend is running and try again.'
            : ((err.response?.data as { detail?: string } | undefined)?.detail || 'Invalid credentials');
        setError(message);
      } else {
        setError('Invalid credentials');
      }
    } finally {
      if (!showBiometricPrompt) {
        setIsLoading(false);
      }
    }
  };

  const afterSignIn = () => {
    if (biometricAvailable && !biometricEnabled) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        setPendingCredentials({ username, refreshToken });
        setShowBiometricPrompt(true);
        setIsLoading(false);
        return;
      }
    }
    navigate('/');
  };

  const detailOf = (err: unknown, fallback: string) =>
    (axios.isAxiosError(err) && (err.response?.data as { detail?: string } | undefined)?.detail) || fallback;

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFactor || code.replace(/\D/g, '').length !== 6) return;
    setError('');
    setIsLoading(true);
    try {
      await completeTwoFactor(username, twoFactor.challenge, code, rememberDevice);
      afterSignIn();
    } catch (err: unknown) {
      setError(detailOf(err, "That code didn't work. Try again."));
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!twoFactor || resendIn > 0) return;
    setError('');
    try {
      const fresh = await authAPI.resendTwoFactor(twoFactor.challenge);
      setTwoFactor({ ...twoFactor, ...fresh });
      setCode('');
      setResendIn(30);
    } catch (err: unknown) {
      setError(detailOf(err, "Couldn't send a new code. Try again in a moment."));
    }
  };

  const handleEnableBiometric = async () => {
    if (pendingCredentials) {
      try {
        await enableBiometricLogin(pendingCredentials.username, pendingCredentials.refreshToken);
      } catch {
        // continue even if enabling fails
      }
    }
    setShowBiometricPrompt(false);
    setPendingCredentials(null);
    navigate('/');
  };

  const handleDeclineBiometric = () => {
    setShowBiometricPrompt(false);
    setPendingCredentials(null);
    navigate('/');
  };

  const biometryLabel = getBiometryLabel(biometryType);
  const BiometricIcon = biometryType === 'face' ? ScanFace : Fingerprint;

  const showDemoAccounts = import.meta.env.VITE_SHOW_DEMO_ACCOUNTS === 'true';

  // Demo account buttons
  const demoAccounts = [
    { username: 'preston.member', label: 'Preston Brown', role: 'Member' },
    { username: 'marc.advisor', label: 'Marc Carson', role: 'Advisor' },
    { username: 'chris.advisor', label: 'Chris Nedza', role: 'Advisor' },
    { username: 'barry.facilitator', label: 'Barry Lusk', role: 'Facilitator' },
    { username: 'jonathan.admin', label: 'Jonathan Almanzar', role: 'Admin' },
  ];

  const loginAsDemo = (demoUsername: string) => {
    setUsername(demoUsername);
    setPassword('demo123');
  };

  // Biometric opt-in modal
  if (showBiometricPrompt) {
    return (
      <div className="standalone-screen flex justify-center p-4">
        <div className="w-full max-w-sm m-auto bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-navy/10 rounded-full mb-4">
            <BiometricIcon className="w-8 h-8 text-navy" />
          </div>
          <h2 className="text-xl font-semibold text-navy mb-2">
            Enable {biometryLabel}?
          </h2>
          <p className="text-gray-600 text-sm mb-6">
            Sign in faster next time using {biometryLabel.toLowerCase()} instead of your password.
          </p>
          <div className="space-y-3">
            <button
              onClick={handleEnableBiometric}
              className="w-full bg-navy text-white py-3 rounded-lg font-semibold hover:bg-navy-dark transition"
            >
              Enable {biometryLabel}
            </button>
            <button
              onClick={handleDeclineBiometric}
              className="w-full text-gray-500 py-3 rounded-lg font-medium hover:bg-gray-100 transition"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (twoFactor) {
    return (
      <div className="standalone-screen flex justify-center p-4">
        <div className="w-full max-w-md m-auto">
          <div className="text-center mb-8">
            <BrandLogo className="h-28 w-28 mx-auto" />
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-navy/10 rounded-full mb-3">
                <Mail className="w-7 h-7 text-navy" />
              </div>
              <h2 className="text-xl font-semibold text-navy">Check your email</h2>
              <p className="text-sm text-gray-600 mt-1">
                We sent a 6-digit code to <span className="font-medium">{twoFactor.email_hint}</span>. It expires in 10 minutes.
              </p>
            </div>
            <form onSubmit={handleVerify} className="space-y-4">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                aria-label="6-digit code"
                placeholder="000000"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-center text-2xl tracking-[0.5em] font-semibold text-navy focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="w-4 h-4 accent-navy"
                />
                Remember this device for 30 days
              </label>
              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                  <X className="w-4 h-4 mt-0.5 flex-shrink-0 cursor-pointer" onClick={() => setError('')} />
                  <span>{error}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={isLoading || code.length !== 6}
                className="w-full bg-navy text-white py-3 rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50"
              >
                {isLoading ? 'Checking...' : 'Verify and sign in'}
              </button>
            </form>
            <div className="mt-4 flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => { setTwoFactor(null); setError(''); setCode(''); }}
                className="text-gray-500 hover:text-navy"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendIn > 0}
                className="text-navy font-medium hover:text-gold disabled:text-gray-400"
              >
                {resendIn > 0 ? `Send a new code in ${resendIn}s` : 'Send a new code'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="standalone-screen flex justify-center p-4">
      <div className="w-full max-w-md m-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <BrandLogo className="h-28 w-28 mx-auto" />
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-semibold text-navy mb-6 text-center">
            Sign in to your account
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email or username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none transition"
                placeholder="Enter your email or username"
                autoComplete="username"
                autoCapitalize="none"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-navy hover:text-gold font-medium">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none transition"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 pointer-coarse:right-1 pointer-coarse:p-2.5"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0 cursor-pointer" onClick={() => setError('')} />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-navy text-white py-3 rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Biometric Login */}
          {biometricEnabled && (
            <div className="mt-4">
              <div className="relative flex items-center justify-center my-2">
                <div className="border-t border-gray-200 w-full" />
                <span className="bg-white px-3 text-sm text-gray-400 absolute">or</span>
              </div>
              <button
                onClick={handleBiometricLogin}
                disabled={isLoading}
                className="w-full mt-2 flex items-center justify-center gap-2 py-3 rounded-lg border-2 border-navy text-navy font-semibold hover:bg-navy hover:text-white transition disabled:opacity-50"
              >
                <BiometricIcon className="w-5 h-5" />
                Sign in with {biometryLabel}
              </button>
            </div>
          )}

          {/* Demo Accounts */}
          {showDemoAccounts && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center mb-4">
                Demo Accounts (password: demo123)
              </p>
              <div className="grid gap-2">
                {demoAccounts.map((account) => (
                  <button
                    key={account.username}
                    onClick={() => loginAsDemo(account.username)}
                    className="w-full py-2 px-4 rounded-lg border border-navy text-navy hover:bg-navy hover:text-white transition text-sm font-medium"
                  >
                    {account.label} ({account.role})
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Legal footer */}
        <p className="text-center text-xs text-gray-500 mt-6">
          By signing in you agree to our{' '}
          <Link to="/terms" className="text-navy underline hover:text-gold">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-navy underline hover:text-gold">
            Privacy Policy
          </Link>
          {' '}or visit{' '}
          <Link to="/support" className="text-navy underline hover:text-gold">
            Support
          </Link>
          . How we protect your data:{' '}
          <Link to="/security" className="text-navy underline hover:text-gold">
            Security
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
